import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Clock, ArrowRight } from "lucide-react";

/**
 * StoreShowcase — Premium "Unser Geschäft" Sektion mit echten Fotos
 * Asymmetrisches Bento-Layout: Schaufenster groß links, Innenraum klein rechts,
 * Adresse/Öffnungszeiten/CTA als Glassmorphism-Karte.
 */
export default function StoreShowcase() {
  return (
    <section
      className="relative py-24 md:py-32 bg-[#0A0A0B] overflow-hidden"
      data-testid="store-showcase"
      id="store"
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#DC1928]/8 blur-[120px] pointer-events-none"></div>
      <div className="absolute -bottom-40 -right-32 w-[600px] h-[600px] rounded-full bg-white/[0.02] blur-[140px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        {/* Section heading */}
        <div className="grid md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Unser Geschäft</div>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight text-white mt-3 leading-[1.02]">
              Persönlich.<br/>
              <span className="text-zinc-500">Mitten in Wien.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-4 flex items-end">
            <p className="text-lg text-zinc-400 leading-relaxed">
              Hernalser Hauptstraße 135, 1170 Wien. Kein Callcenter — echte Menschen,
              echte Geräte, echte Reparaturen.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid md:grid-cols-12 grid-rows-[auto_auto] md:grid-rows-2 gap-5 md:gap-6">

          {/* LARGE: Storefront night photo */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-8 md:row-span-2 relative group"
            data-testid="store-showcase-storefront"
          >
            <div className="relative h-[420px] md:h-full overflow-hidden rounded-[28px] border border-white/10">
              <img
                src="/images/store/storefront-night-new.jpg"
                alt="Haus des Handys Schaufenster — Hernalser Hauptstraße 135, 1170 Wien"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
              {/* Vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent md:to-black/30"></div>

              {/* Bottom address */}
              <div className="absolute bottom-0 left-0 right-0 p-7 md:p-10">
                <div className="flex items-center gap-2 text-[#FF2D40] mb-3">
                  <MapPin className="w-4 h-4" />
                  <span className="text-[11px] uppercase tracking-[0.22em] font-bold">Adresse</span>
                </div>
                <div className="font-display text-2xl md:text-4xl font-bold text-white leading-tight">
                  Hernalser Hauptstraße 135
                </div>
                <div className="font-display text-lg md:text-2xl font-medium text-zinc-300 mt-1">
                  1170 Wien · Österreich
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT: Opening hours card (Zusatzleistungen-Kachel entfernt) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-4 md:row-span-2 relative"
            data-testid="store-showcase-hours"
          >
            <div className="relative h-full p-7 rounded-[24px] bg-gradient-to-br from-[#141417] to-[#0F0F12] border border-white/10 overflow-hidden grain">
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#DC1928]/15 blur-3xl"></div>

              <div className="relative">
                <div className="flex items-center gap-2 text-[#FF2D40]">
                  <Clock className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-[0.24em] font-bold">Öffnungszeiten</span>
                </div>

                <div className="mt-5 space-y-2.5">
                  <Row label="Mo – Fr" value="09:00 – 19:00" />
                  <Row label="Samstag" value="09:00 – 16:00" />
                  <Row label="Mittagspause" value="12:30 – 13:00" muted />
                  <Row label="Sonntag" value="Geschlossen" muted />
                </div>

                <div className="mt-6 pt-5 border-t border-white/5">
                  <Link to="/kontakt" data-testid="store-showcase-contact">
                    <div className="text-sm text-white font-medium inline-flex items-center gap-1.5 group">
                      Termin vereinbaren
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex justify-between items-baseline text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className={`font-display font-semibold numeric ${muted ? "text-zinc-500" : "text-white"}`}>{value}</span>
    </div>
  );
}
